import React, { useMemo, useState } from "react";
import { formatDistance } from "date-fns";
import { useSearchParams } from "react-router-dom";
import GoogleMaps from "components/GoogleMapBox";
import {
  SummaryCard,
  DeviceInfoSection,
  MapSection,
  SectionTitle,
  DetailRow,
  DetailLabel,
  DetailValue,
  ConnectionStatus,
  EditIcon,
  AddressContainer,
} from "styles/devices/deviceSummary.styles";
import {
  useDevice,
  useDeviceStatus,
  useUpdateDeviceLinkAlias,
} from "hooks/useDevices";
import { EditAliasModal } from "./EditAliasModal";
import { notify } from "utils/notify";
import { useAuth } from "context/AuthContext";

interface DeviceLocation {
  address?: string;
  latitude?: number;
  longitude?: number;
}

interface DeviceSummaryProps {
  serialNumber: string;
  firmwareVersion?: string;
  nipperVersion?: string;
  updatedAt: string;
  location?: DeviceLocation;
  onEditAddressClick: () => void;
}

function DeviceSummary({
  serialNumber,
  firmwareVersion,
  nipperVersion,
  updatedAt,
  location,
  onEditAddressClick,
}: DeviceSummaryProps) {
  const [searchParams] = useSearchParams();
  const linkIdParam = searchParams.get("linkId");
  const linkId =
    linkIdParam && linkIdParam !== "undefined" && linkIdParam !== "null"
      ? linkIdParam
      : undefined;
  const [aliasModalVisible, setAliasModalVisible] = useState(false);
  const { user } = useAuth();

  const { data, refetch } = useDevice(serialNumber, linkId);
  const { data: deviceStatus } = useDeviceStatus(serialNumber);
  const updateAliasMP = useUpdateDeviceLinkAlias();

  const mapMarkers = useMemo(() => {
    if (!location?.latitude || !location?.longitude) return undefined;
    return [
      {
        lat: location.latitude,
        lng: location.longitude,
        label: serialNumber,
      },
    ];
  }, [location?.latitude, location?.longitude, serialNumber]);

  const formatUpdatedDate = (dateString: string) => {
    try {
      return formatDistance(new Date(dateString), new Date(), {
        addSuffix: true,
      });
    } catch {
      return "Unknown";
    }
  };

  const handleAliasUpdated = async (newAlias: string) => {
    if (!linkId) return;

    try {
      await updateAliasMP.mutateAsync({
        linkId: parseInt(linkId),
        linkAlias: newAlias,
      });
      notify.success("Device alias updated successfully!");
      setAliasModalVisible(false);
      refetch();
    } catch (error) {
      console.error("Error updating alias:", error);
      notify.error("Failed to update device alias. Please try again.");
    }
  };

  const isEu = data?.region === 1;
  const nipperName = isEu ? "EcoSalt2" : "Nipper";
  const isConnected = !!deviceStatus?.response?.connected;
  const canEditAlias = user?.role !== "ADMIN" && !!linkId;

  return (
    <SummaryCard>
      <DeviceInfoSection>
        <DetailLabel>Serial Number:</DetailLabel>
        <SectionTitle>{serialNumber}</SectionTitle>

        <DetailRow>
          <DetailLabel>Lifeguard Version</DetailLabel>
          <DetailValue>{firmwareVersion || "N/A"}</DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>{nipperName} Version</DetailLabel>
          <DetailValue>{nipperVersion || "N/A"}</DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Last Updated</DetailLabel>
          <DetailValue>{formatUpdatedDate(updatedAt)}</DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Connection</DetailLabel>
          <DetailValue>
            <ConnectionStatus connected={isConnected}>
              <i
                className={`pi ${isConnected ? "pi-wifi" : "pi-times-circle"}`}
                style={{ marginRight: "6px" }}
                aria-hidden
              />
              {isConnected ? "Online" : "Offline"}
            </ConnectionStatus>
          </DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Address</DetailLabel>
          <DetailValue>
            <AddressContainer>
              {location?.address || "No address available"}
              <EditIcon
                className="pi pi-pencil"
                role="button"
                tabIndex={0}
                aria-label="Edit device address"
                onClick={onEditAddressClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onEditAddressClick();
                  }
                }}
              />
            </AddressContainer>
          </DetailValue>
        </DetailRow>

        {canEditAlias && (
          <DetailRow>
            <DetailLabel>Alias</DetailLabel>
            <DetailValue>
              <AddressContainer>
                {data?.linkAlias || "No alias set"}
                <EditIcon
                  className="pi pi-pencil"
                  role="button"
                  tabIndex={0}
                  aria-label="Edit device alias"
                  onClick={() => setAliasModalVisible(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setAliasModalVisible(true);
                    }
                  }}
                />
              </AddressContainer>
            </DetailValue>
          </DetailRow>
        )}
      </DeviceInfoSection>

      <MapSection>
        {location?.latitude && location?.longitude ? (
          <GoogleMaps
            address={location.address || "Unknown Address"}
            latitude={location.latitude}
            longitude={location.longitude}
            zoom={15}
            markers={mapMarkers}
          />
        ) : (
          <div>No location data available</div>
        )}
      </MapSection>

      {canEditAlias && (
        <EditAliasModal
          visible={aliasModalVisible}
          linkId={parseInt(linkId!)}
          currentAlias={data?.linkAlias || ""}
          serialNumber={serialNumber}
          onHide={() => setAliasModalVisible(false)}
          onAliasUpdated={handleAliasUpdated}
          isUpdating={updateAliasMP.isPending}
        />
      )}
    </SummaryCard>
  );
}

export default React.memo(DeviceSummary);
